import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, Briefcase, Clock, Search, Plus, Calendar, TrendingUp } from "lucide-react";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import { cn } from "@/lib/utils";

const getDisplayName = (user) => {
  if (user.first_name || user.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user.full_name || user.email;
};

const statusColors = {
  available: "bg-emerald-100 text-emerald-700",
  partially_available: "bg-amber-100 text-amber-700",
  unavailable: "bg-rose-100 text-rose-700",
};

export default function ResourceAllocationBoard({ users, projects, tasks, timeEntries = [] }) {
  const [search, setSearch] = useState("");
  const [skillFilter, setSkillFilter] = useState("all");
  const [viewMode, setViewMode] = useState("week"); // "week" or "month"

  // Get all unique skills
  const allSkills = [...new Set(users.flatMap(u => u.skills || []))];

  // Get period dates
  const periodStart = viewMode === "week" 
    ? startOfWeek(new Date(), { weekStartsOn: 1 })
    : startOfMonth(new Date());
  const periodEnd = viewMode === "week"
    ? endOfWeek(new Date(), { weekStartsOn: 1 })
    : endOfMonth(new Date());

  // Calculate workload for each user based on selected period
  const getUserWorkload = (userEmail) => {
    const userTasks = tasks.filter(task => {
      if (task.assigned_to !== userEmail) return false;
      if (task.status === "completed" || task.status === "cancelled") return false;
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      return isWithinInterval(dueDate, { start: periodStart, end: periodEnd });
    });
    
    const periodTimeEntries = timeEntries.filter(entry => {
      if (entry.user_email !== userEmail) return false;
      const entryDate = new Date(entry.start_time);
      return isWithinInterval(entryDate, { start: periodStart, end: periodEnd });
    });

    const estimatedHours = userTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
    const actualHours = periodTimeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
    const totalHours = estimatedHours + actualHours;
    
    return { taskCount: userTasks.length, totalHours, estimatedHours, actualHours, tasks: userTasks };
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = getDisplayName(user).toLowerCase().includes(search.toLowerCase()) ||
                         user.email?.toLowerCase().includes(search.toLowerCase());
    const matchesSkill = skillFilter === "all" || user.skills?.includes(skillFilter);
    return matchesSearch && matchesSkill;
  });

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === "week" ? "default" : "outline"}
          onClick={() => setViewMode("week")}
          className="flex items-center gap-2"
        >
          <Calendar className="w-4 h-4" />
          Denne Uken
        </Button>
        <Button
          variant={viewMode === "month" ? "default" : "outline"}
          onClick={() => setViewMode("month")}
          className="flex items-center gap-2"
        >
          <TrendingUp className="w-4 h-4" />
          Denne Måneden
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 bg-white border-0 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Søk etter ressurser..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={skillFilter} onValueChange={setSkillFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filtrer etter kompetanse" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle kompetanser</SelectItem>
              {allSkills.map(skill => (
                <SelectItem key={skill} value={skill}>{skill}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Resource Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsers.map(user => {
          const workload = getUserWorkload(user.email);
          const capacity = viewMode === "week" 
            ? (user.weekly_capacity || 37.5)
            : ((user.weekly_capacity || 37.5) * 4.33);
          const utilizationPercent = (workload.totalHours / capacity) * 100;
          const status = user.availability_status || "available";

          return (
            <Card key={user.id} className="p-5 bg-white border-0 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-12 h-12">
                    <AvatarFallback className="bg-gradient-to-br from-lime-100 to-lime-200 text-zinc-900">
                      {getDisplayName(user).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-slate-900">{getDisplayName(user)}</h3>
                    <p className="text-sm text-slate-500">{user.email}</p>
                  </div>
                </div>
                <Badge className={cn("border-0", statusColors[status])}>
                  {status === "available" ? "Tilgjengelig" : 
                   status === "partially_available" ? "Delvis" : "Opptatt"}
                </Badge>
              </div>

              {/* Skills */}
              {user.skills && user.skills.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 mb-2">Kompetanser:</p>
                  <div className="flex flex-wrap gap-1">
                    {user.skills.map(skill => (
                      <Badge key={skill} variant="outline" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Workload */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <p className="text-xs text-emerald-600 mb-1">Utført</p>
                    <p className="text-lg font-bold text-emerald-700">{workload.actualHours.toFixed(1)}h</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-xs text-amber-600 mb-1">Planlagt</p>
                    <p className="text-lg font-bold text-amber-700">{workload.estimatedHours.toFixed(1)}h</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Totalt:</span>
                  <span className="font-medium text-slate-900">
                    {workload.totalHours.toFixed(1)}h / {capacity}h
                  </span>
                </div>

                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className={cn(
                      "h-full transition-all rounded-full",
                      utilizationPercent < 80 ? "bg-emerald-500" :
                      utilizationPercent < 100 ? "bg-amber-500" : "bg-rose-500"
                    )}
                    style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                  />
                </div>

                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    <span>{workload.taskCount} oppgaver</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{utilizationPercent.toFixed(0)}% utnyttelse</span>
                  </div>
                </div>
              </div>

              {/* Active Projects */}
              {workload.tasks.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Aktive prosjekter:</p>
                  <div className="space-y-1">
                    {[...new Set(workload.tasks.map(t => t.project_name))].filter(Boolean).slice(0, 3).map(projectName => (
                      <div key={projectName} className="text-xs text-slate-600 truncate">
                        • {projectName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}